import { FolderOpen, MonitorPlay, Presentation, Video } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { listMedia } from "@/features/knowledge/api";
import type { KbMediaAsset } from "@/features/knowledge/types";

// Fixed library categories (root slugs). The "tooltips" root is deliberately
// excluded from the public library (legacy "jimmies" node).
const CATEGORIES = [
  { slug: "software", to: "/kb/library/software", icon: FolderOpen },
  { slug: "resources", to: "/kb/library/resources", icon: Presentation },
  { slug: "webinars", to: "/kb/library/webinars", icon: MonitorPlay },
  { slug: "resolution", to: "/kb/library/resolution", icon: Video },
];

export default function LibraryHomePage() {
  const [roots, setRoots] = useState<KbMediaAsset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    listMedia("root")
      .then((data) => !cancelled && setRoots(data))
      .catch(() => !cancelled && toast.error("Could not load the library."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  const bySlug = new Map(roots.map((r) => [r.slug, r]));

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {CATEGORIES.map(({ slug, to, icon: Icon }) => {
        const node = bySlug.get(slug);
        return (
          <Link key={slug} to={to}>
            <Card className="hover:shadow-card-hover h-full p-5 transition-shadow">
              <Icon className="text-primary mb-3 size-6" />
              <p className="font-semibold">{node?.description ?? slug}</p>
              <p className="text-muted-foreground text-sm">Browse this category</p>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}
